import { EphemerisDisplay, EphemerisDisplayCell, EphemerisDisplayRowKind } from './ephemeris-display.utils';

/** Vertaalde labels voor de export (de component levert ze aan in de taal van de gebruiker). */
export interface EphemerisExportLabels {
  readonly title: string;
  /** Gekozen opties, bv. "Dagposities · Zodiac · Lokaal (Europe/Brussels) · 6 okt 2026 – 20 okt 2026". */
  readonly options: string;
  readonly date: string;
  readonly time: string;
  readonly rowType: string;
  readonly rowTypes: Readonly<Record<EphemerisDisplayRowKind, string>>;
  /** Kolomkoppen per kolom van de weergave, bv. "☉ Zon" of "☽ Maan +6u". */
  readonly columns: readonly string[];
}

export interface EphemerisExportRow {
  readonly kind: EphemerisDisplayRowKind;
  /** Dag- of maandkop vóór deze rij (Excel toont hem als tussenkop). */
  readonly groupLabel: string | undefined;
  /** `YYYY-MM-DD` in de weergavetijdzone. */
  readonly date: string;
  /** `HH:mm:ss` in de weergavetijdzone. */
  readonly time: string;
  readonly cells: readonly EphemerisDisplayCell[];
}

/** Wat je ziet, klaar voor CSV of Excel. */
export interface EphemerisExportSheet {
  readonly labels: EphemerisExportLabels;
  readonly lang: string;
  readonly rows: readonly EphemerisExportRow[];
  /** Bestandsnaam zonder extensie. */
  readonly fileBaseName: string;
}

/** Astronomicon-tekenletters (A = Ram ... L = Vissen) → Unicode-tekensymbolen voor export. */
const ZODIAC_UNICODE = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];

export function buildEphemerisExportSheet(
  display: EphemerisDisplay,
  labels: EphemerisExportLabels,
  lang: string,
  fileBaseName: string,
): EphemerisExportSheet {
  return {
    labels,
    lang,
    fileBaseName,
    rows: display.rows.map(row => ({
      kind: row.kind,
      groupLabel: row.groupLabel,
      date: row.at.format('YYYY-MM-DD'),
      time: row.at.format('HH:mm:ss'),
      cells: row.cells,
    })),
  };
}

/**
 * Celtekst zoals op het scherm, met Unicode i.p.v. het Astronomicon-font: `32.4 ℞`,
 * `48.3 S℞`, `27°04'50" ♐ ℞`.
 */
export function ephemerisExportCellText(cell: EphemerisDisplayCell): string {
  const sign = cell.signGlyph ? ` ${ZODIAC_UNICODE[cell.signGlyph.charCodeAt(0) - 'A'.charCodeAt(0)] ?? ''}` : '';
  const marker =
    cell.station === 'retrograde' ? ' S℞' : cell.station === 'direct' ? ' SD' : cell.retrograde ? ' ℞' : '';
  return `${cell.text}${sign}${marker}`;
}

/**
 * CSV van de weergave. Scheidingsteken en decimaalteken volgen de taal (NL: `;` en `,`,
 * zoals Excel in die taal verwacht); UTF-8 met BOM zodat Excel de symbolen goed leest.
 * Graden komen als getal (zonder °), zodat ze rekenbaar blijven.
 */
export function buildEphemerisCsv(sheet: EphemerisExportSheet): string {
  const dutch = sheet.lang.toLowerCase().startsWith('nl');
  const separator = dutch ? ';' : ',';
  const number = new Intl.NumberFormat(dutch ? 'nl' : 'en', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    useGrouping: false,
  });
  // Enkel quoten wat moet: een NL-decimale komma tussen aanhalingstekens leest Excel als tekst.
  const quote = (value: string): string =>
    value.includes(separator) || /["\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

  const header = [sheet.labels.date, sheet.labels.time, ...sheet.labels.columns, sheet.labels.rowType];
  const lines = [header.map(quote).join(separator)];
  for (const row of sheet.rows) {
    const cells = row.cells.map(cell => (cell.degrees !== null ? number.format(cell.degrees) : ephemerisExportCellText(cell)));
    lines.push([row.date, row.time, ...cells, sheet.labels.rowTypes[row.kind]].map(quote).join(separator));
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
